/**
 * Request validation, shared so the admin forms and the API agree on what a
 * valid payload is. The API is the enforcer; the clients use these to give
 * useful errors before a round trip.
 */

import { z } from 'zod';
import {
  ACCESS_CREDENTIAL_KIND, ACCESS_CREDENTIAL_STATE, ACCESS_DECISION, ACCESS_PROVIDER,
  AUDIENCE, BONSPIEL_KIND, CHARGE_KIND, CHECKIN_METHOD, DOC_CATEGORY, DUTY_KIND, DUTY_STATUS,
  ENTRY_MODE, ENTRY_STATUS, EVENT_KIND, GAME_STATUS, INVOICE_STATUS, LEAGUE_FORMAT,
  LEAGUE_GENDER, MEMBER_STATUS, PAYMENT_METHOD, POSITION, RENTAL_STATUS, ROLES,
  SPONSOR_TARGET, SPONSOR_TIER, PAYMENT_CHOICE, REGISTRATION_STATUS,
} from './enums';
import {
  WAIVER_CONTAINER_STYLES, WAIVER_FIELD_TYPES, WAIVER_SIGNATURE_MODES,
  WAIVER_SIGNER_ROLES, WAIVER_VISIBILITY_OPERATORS,
} from './waiver-doc';

const uuid = z.string().uuid();
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected HH:MM');
const email = z.string().trim().toLowerCase().email();
const phone = z.string().trim().min(7).max(32);
const nonEmpty = z.string().trim().min(1);
const cents = z.number().int().min(0);

const nullable = <T extends z.ZodTypeAny>(s: T) => s.nullish().transform((v) => v ?? null);

/* ---------------------------------------------------------------- Auth ---- */

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'Password is required'),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(10, 'Use at least 10 characters'),
});

export const requestResetSchema = z.object({ email });

export const performResetSchema = z.object({
  token: nonEmpty,
  newPassword: z.string().min(10, 'Use at least 10 characters'),
});

/* ------------------------------------------------------------- Members ---- */

export const memberSchema = z.object({
  firstName: nonEmpty.max(80),
  lastName: nonEmpty.max(80),
  email: nullable(email),
  phone: nullable(phone),
  address: nullable(z.string().trim().max(200)),
  city: nullable(z.string().trim().max(80)),
  province: nullable(z.string().trim().max(40)),
  postalCode: nullable(z.string().trim().max(12)),
  dateOfBirth: nullable(dateOnly),
  status: z.enum(MEMBER_STATUS).default('active'),
  isLifeMember: z.boolean().default(false),
  joinedOn: nullable(dateOnly),
  emergencyContactName: nullable(z.string().trim().max(120)),
  emergencyContactPhone: nullable(phone),
  accessibilityNotes: nullable(z.string().trim().max(500)),
  notes: nullable(z.string().trim().max(2000)),
  photoUrl: nullable(z.string().url()),
});

export const memberUpdateSchema = memberSchema.partial();

export const setRolesSchema = z.object({
  roles: z.array(z.enum(ROLES)).max(ROLES.length),
});

/** What a member may change about themselves in the portal. */
export const selfProfileSchema = memberSchema
  .pick({
    email: true, phone: true, address: true, city: true, province: true,
    postalCode: true, emergencyContactName: true, emergencyContactPhone: true,
    accessibilityNotes: true,
  })
  .partial();

/* ------------------------------------------------------------- Seasons ---- */

export const seasonSchema = z.object({
  name: nonEmpty.max(40),
  startsOn: dateOnly,
  endsOn: dateOnly,
  isCurrent: z.boolean().default(false),
}).refine((v) => v.startsOn <= v.endsOn, {
  message: 'The season cannot end before it starts',
  path: ['endsOn'],
});

export const feeCategorySchema = z.object({
  seasonId: uuid,
  code: nonEmpty.max(40),
  label: nonEmpty.max(120),
  amountCents: cents,
  description: nullable(z.string().trim().max(500)),
  sortOrder: z.number().int().default(0),
});

/* ------------------------------------------------------------- Leagues ---- */

export const drawTimeSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  startTime: hhmm,
});

export const leagueSchema = z.object({
  seasonId: uuid,
  name: nonEmpty.max(120),
  slug: nullable(z.string().trim().max(80)),
  description: nullable(z.string().trim().max(4000)),
  format: z.enum(LEAGUE_FORMAT),
  gender: z.enum(LEAGUE_GENDER).default('open'),
  isCompetitive: z.boolean().default(false),
  capacityTeams: nullable(z.number().int().positive().max(64)),
  minAge: nullable(z.number().int().min(0).max(120)),
  maxAge: nullable(z.number().int().min(0).max(120)),
  endsPerGame: z.number().int().min(2).max(12).default(8),
  schedulesPerSeason: z.number().int().min(1).max(6).default(1),
  registrationOpen: z.boolean().default(true),
  isPublic: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  sponsorText: nullable(z.string().trim().max(1000)),
  drawTimes: z.array(drawTimeSchema).max(8).default([]),
  convenerMemberIds: z.array(uuid).max(8).default([]),
}).refine((v) => v.minAge === null || v.maxAge === null || v.minAge <= v.maxAge, {
  message: 'Minimum age cannot exceed maximum age',
  path: ['maxAge'],
});

export const leagueUpdateSchema = leagueSchema.innerType().partial();

export const registrationSchema = z.object({
  leagueId: uuid,
  memberId: uuid,
  positionPref: nullable(z.enum(POSITION)),
  entryMode: z.enum(ENTRY_MODE).default('single'),
  isSpare: z.boolean().default(false),
  skillRating: nullable(z.number().int().min(1).max(10)),
  teamId: nullable(uuid),
  notes: nullable(z.string().trim().max(500)),
});

/* --------------------------------------------------------------- Teams ---- */

export const teamSchema = z.object({
  leagueId: uuid,
  name: nonEmpty.max(120),
  scheduleNo: z.number().int().min(1).max(6).default(1),
  seed: nullable(z.number().int().min(1).max(64)),
  isActive: z.boolean().default(true),
  players: z.array(z.object({
    memberId: uuid,
    position: z.enum(POSITION),
  })).max(8).default([]),
});

export const teamUpdateSchema = teamSchema.partial();

/** Draw a whole schedule's teams from the pool of registered individuals. */
export const drawTeamsSchema = z.object({
  scheduleNo: z.number().int().min(1).max(6),
  teamSize: z.number().int().min(2).max(5).default(4),
  /** Balance teams by skill rating rather than drawing at random. */
  balanceBySkill: z.boolean().default(true),
  /** Keep these members together, e.g. couples in Curl & Dine. */
  keepTogether: z.array(z.array(uuid).min(2).max(5)).default([]),
  namePattern: z.string().trim().max(40).default('Team {skip}'),
  replaceExisting: z.boolean().default(false),
});

/* --------------------------------------------------------------- Draws ---- */

export const generateScheduleSchema = z.object({
  leagueId: uuid,
  scheduleNo: z.number().int().min(1).max(6).default(1),
  startDate: dateOnly,
  gamesPerTeam: z.number().int().min(1).max(40),
  sheetNumbers: z.array(z.number().int().min(1).max(12)).min(1).default([1, 2, 3, 4, 5, 6]),
  blackoutDates: z.array(dateOnly).max(40).default([]),
  /** Preview only — do not write anything. */
  dryRun: z.boolean().default(false),
  replaceExisting: z.boolean().default(false),
});

export const drawSchema = z.object({
  leagueId: uuid,
  drawNumber: z.number().int().min(1),
  scheduleNo: z.number().int().min(1).max(6).default(1),
  scheduledAt: z.string().datetime({ offset: true }),
  notes: nullable(z.string().trim().max(500)),
});

export const gameSchema = z.object({
  drawId: uuid,
  sheetId: nullable(uuid),
  homeTeamId: nullable(uuid),
  awayTeamId: nullable(uuid),
  endsPlanned: z.number().int().min(2).max(12).default(8),
  eventCode: nullable(z.string().trim().max(4)),
  round: nullable(z.number().int().min(1).max(40)),
});

export const endSchema = z.object({
  endNumber: z.number().int().min(1).max(20),
  homePoints: z.number().int().min(0).max(8),
  awayPoints: z.number().int().min(0).max(8),
}).refine((v) => v.homePoints === 0 || v.awayPoints === 0, {
  message: 'An end cannot be scored by both teams',
  path: ['awayPoints'],
});

export const scoreSchema = z.object({
  ends: z.array(endSchema).max(20).default([]),
  /** Set directly when a scoresheet only recorded the final. */
  homeScore: nullable(z.number().int().min(0).max(60)),
  awayScore: nullable(z.number().int().min(0).max(60)),
  status: z.enum(GAME_STATUS).default('final'),
  forfeitTeamId: nullable(uuid),
  notes: nullable(z.string().trim().max(500)),
});

/* ----------------------------------------------------------- Bonspiels ---- */

export const bonspielSchema = z.object({
  seasonId: uuid,
  name: nonEmpty.max(140),
  slug: nullable(z.string().trim().max(80)),
  kind: z.enum(BONSPIEL_KIND),
  startsOn: dateOnly,
  endsOn: dateOnly,
  description: nullable(z.string().trim().max(4000)),
  entryFeeCents: nullable(cents),
  maxTeams: nullable(z.number().int().positive().max(128)),
  registrationOpen: z.boolean().default(false),
  flyerUrl: nullable(z.string().url()),
  entryFormUrl: nullable(z.string().url()),
  sponsorText: nullable(z.string().trim().max(1000)),
  contactEmail: nullable(email),
  isPublic: z.boolean().default(true),
  eventCodes: z.array(z.string().trim().min(1).max(4)).max(8).default(['A', 'B']),
}).refine((v) => v.startsOn <= v.endsOn, {
  message: 'The spiel cannot end before it starts',
  path: ['endsOn'],
});

export const bonspielUpdateSchema = bonspielSchema.innerType().partial();

export const bonspielEntrySchema = z.object({
  teamName: nonEmpty.max(120),
  homeClub: nullable(z.string().trim().max(120)),
  skipName: nonEmpty.max(120),
  contactEmail: nullable(email),
  contactPhone: nullable(phone),
  players: z.array(z.object({
    name: nonEmpty.max(120),
    position: z.enum(POSITION).optional(),
  })).max(6).default([]),
  notes: nullable(z.string().trim().max(1000)),
});

export const bonspielEntryAdminSchema = bonspielEntrySchema.extend({
  status: z.enum(ENTRY_STATUS).default('pending'),
  isPaid: z.boolean().default(false),
  amountCents: nullable(cents),
});

export const generateBracketSchema = z.object({
  eventCodes: z.array(z.string().trim().min(1).max(4)).min(1).max(8).default(['A', 'B', 'C', 'D']),
  seedBy: z.enum(['entry_order', 'random']).default('entry_order'),
  dryRun: z.boolean().default(false),
});

/* ------------------------------------------------- Events, news, content -- */

export const eventSchema = z.object({
  seasonId: nullable(uuid),
  title: nonEmpty.max(160),
  description: nullable(z.string().trim().max(4000)),
  kind: z.enum(EVENT_KIND).default('other'),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: nullable(z.string().datetime({ offset: true })),
  allDay: z.boolean().default(false),
  location: nullable(z.string().trim().max(160)),
  linkUrl: nullable(z.string().url()),
  isPublic: z.boolean().default(true),
  leagueId: nullable(uuid),
  bonspielId: nullable(uuid),
});

export const postSchema = z.object({
  title: nonEmpty.max(200),
  slug: nullable(z.string().trim().max(100)),
  excerpt: nullable(z.string().trim().max(500)),
  bodyMd: nonEmpty.max(40000),
  heroImageUrl: nullable(z.string().url()),
  audience: z.enum(AUDIENCE).default('public'),
  leagueId: nullable(uuid),
  isPinned: z.boolean().default(false),
  publishedAt: nullable(z.string().datetime({ offset: true })),
});

export const sponsorSchema = z.object({
  name: nonEmpty.max(160),
  url: nullable(z.string().url()),
  logoUrl: nullable(z.string().url()),
  tier: z.enum(SPONSOR_TIER).default('advertiser'),
  category: nullable(z.string().trim().max(80)),
  blurb: nullable(z.string().trim().max(500)),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});

export const sponsorshipSchema = z.object({
  sponsorId: uuid,
  seasonId: nullable(uuid),
  targetType: z.enum(SPONSOR_TARGET),
  targetId: nullable(uuid),
  label: nonEmpty.max(200),
  amountCents: nullable(cents),
});

export const trophySchema = z.object({
  name: nonEmpty.max(160),
  description: nullable(z.string().trim().max(1000)),
  leagueId: nullable(uuid),
  sponsorId: nullable(uuid),
});

export const trophyWinnerSchema = z.object({
  seasonId: uuid,
  teamId: nullable(uuid),
  memberNames: nullable(z.string().trim().max(400)),
  awardedOn: nullable(dateOnly),
});

export const gallerySchema = z.object({
  title: nonEmpty.max(160),
  slug: nullable(z.string().trim().max(100)),
  description: nullable(z.string().trim().max(1000)),
  seasonId: nullable(uuid),
});

export const photoSchema = z.object({
  url: z.string().url(),
  thumbUrl: nullable(z.string().url()),
  caption: nullable(z.string().trim().max(400)),
  takenOn: nullable(dateOnly),
  sortOrder: z.number().int().default(0),
});

export const documentSchema = z.object({
  title: nonEmpty.max(200),
  category: z.enum(DOC_CATEGORY).default('other'),
  url: z.string().url(),
  seasonId: nullable(uuid),
  isPublic: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});

/* ------------------------------------------------------------- Rentals ---- */

export const rentalSpaceSchema = z.object({
  name: nonEmpty.max(120),
  description: nullable(z.string().trim().max(2000)),
  capacity: nullable(z.number().int().positive().max(2000)),
  hourlyCents: nullable(cents),
  dailyCents: nullable(cents),
  isAccessible: z.boolean().default(true),
  isActive: z.boolean().default(true),
});

export const rentalBookingSchema = z.object({
  spaceId: uuid,
  contactName: nonEmpty.max(140),
  contactEmail: nullable(email),
  contactPhone: nullable(phone),
  organisation: nullable(z.string().trim().max(160)),
  purpose: nullable(z.string().trim().max(500)),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  attendees: nullable(z.number().int().positive().max(2000)),
  needsKitchen: z.boolean().default(false),
  needsBar: z.boolean().default(false),
  needsLinen: z.boolean().default(false),
  notes: nullable(z.string().trim().max(2000)),
}).refine((v) => v.startsAt < v.endsAt, {
  message: 'The booking cannot end before it starts',
  path: ['endsAt'],
});

export const rentalStatusSchema = z.object({
  status: z.enum(RENTAL_STATUS),
  quotedCents: nullable(cents),
  notes: nullable(z.string().trim().max(2000)),
});

/* --------------------------------------------------------- Money -------- */

export const invoiceSchema = z.object({
  memberId: nullable(uuid),
  contactName: nullable(z.string().trim().max(140)),
  seasonId: nullable(uuid),
  issuedOn: dateOnly,
  dueOn: nullable(dateOnly),
  taxCents: cents.default(0),
  notes: nullable(z.string().trim().max(2000)),
  items: z.array(z.object({
    description: nonEmpty.max(300),
    quantity: z.number().min(0.01).max(9999).default(1),
    unitCents: cents,
    kind: z.enum(CHARGE_KIND).default('other'),
    refType: nullable(z.string().trim().max(40)),
    refId: nullable(uuid),
  })).min(1, 'An invoice needs at least one line'),
});

export const invoiceStatusSchema = z.object({ status: z.enum(INVOICE_STATUS) });

export const paymentSchema = z.object({
  amountCents: z.number().int().positive(),
  method: z.enum(PAYMENT_METHOD),
  reference: nullable(z.string().trim().max(120)),
  receivedOn: dateOnly,
});

/** Raise membership invoices for a whole season in one go. */
export const billSeasonSchema = z.object({
  seasonId: uuid,
  issuedOn: dateOnly,
  dueOn: nullable(dateOnly),
  onlyUnbilled: z.boolean().default(true),
  dryRun: z.boolean().default(true),
});

/* -------------------------------------------------------------- Duties ---- */

export const dutySchema = z.object({
  seasonId: uuid,
  kind: z.enum(DUTY_KIND),
  title: nonEmpty.max(160),
  occursOn: dateOnly,
  leagueId: nullable(uuid),
  slots: z.number().int().min(1).max(30).default(1),
  notes: nullable(z.string().trim().max(1000)),
});

export const dutyAssignSchema = z.object({
  memberIds: z.array(uuid).min(1).max(30),
});

export const dutyStatusSchema = z.object({ status: z.enum(DUTY_STATUS) });

/* ------------------------------------------------------ Communications ---- */

export const pushTokenSchema = z.object({
  token: nonEmpty.max(300),
  platform: z.enum(['ios', 'android', 'web']),
});

export const broadcastSchema = z.object({
  subject: nonEmpty.max(200),
  bodyMd: nonEmpty.max(20000),
  audience: z.object({
    scope: z.enum(['all_members', 'league', 'team', 'roles', 'bonspiel_entrants']),
    leagueId: nullable(uuid),
    teamId: nullable(uuid),
    bonspielId: nullable(uuid),
    roles: z.array(z.enum(ROLES)).default([]),
  }),
  channels: z.array(z.enum(['email', 'push'])).min(1).default(['email']),
  dryRun: z.boolean().default(true),
});

/* ---------------------------------------------------------- Query params -- */

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});

export const memberQuerySchema = paginationSchema.extend({
  q: z.string().trim().max(120).optional(),
  status: z.enum(MEMBER_STATUS).optional(),
  seasonId: uuid.optional(),
  leagueId: uuid.optional(),
  role: z.enum(ROLES).optional(),
  sort: z.enum(['name', 'joined', 'status']).default('name'),
});

export const scheduleQuerySchema = z.object({
  leagueId: uuid.optional(),
  seasonId: uuid.optional(),
  scheduleNo: z.coerce.number().int().min(1).max(6).optional(),
  from: dateOnly.optional(),
  to: dateOnly.optional(),
  teamId: uuid.optional(),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type MemberInput = z.infer<typeof memberSchema>;
export type LeagueInput = z.infer<typeof leagueSchema>;
export type TeamInput = z.infer<typeof teamSchema>;
export type ScoreInput = z.infer<typeof scoreSchema>;
export type GenerateScheduleInput = z.infer<typeof generateScheduleSchema>;
export type BonspielInput = z.infer<typeof bonspielSchema>;
export type BonspielEntryInput = z.infer<typeof bonspielEntrySchema>;
export type RentalBookingInput = z.infer<typeof rentalBookingSchema>;
export type InvoiceInput = z.infer<typeof invoiceSchema>;
export type BroadcastInput = z.infer<typeof broadcastSchema>;

/* ------------------------------------------------- digital signatures ----- */

/** A single drawn stroke, normalised to 0..1 against the capture box. */
export const strokePointSchema = z.object({
  x: z.number().min(-0.05).max(1.05),
  y: z.number().min(-0.05).max(1.05),
});

/**
 * What a signing act consists of: the name typed, and the signature drawn.
 * Both are required — a tick alone is what this replaces.
 */
export const drawnSignatureSchema = z.object({
  typedName: z.string().trim().min(2, 'Type your full name').max(120),
  strokes: z.array(z.array(strokePointSchema).max(4000)).min(1, 'Draw your signature').max(200),
  /** width / height of the box it was drawn in, so it redraws in proportion. */
  aspect: z.number().min(0.2).max(20).default(3),
  /** For a youth waiver, the parent or guardian doing the signing. */
  guardianName: nullable(z.string().trim().max(120)),
});

export const signWaiverSchema = drawnSignatureSchema.extend({
  memberId: uuid.optional(),
});

export type DrawnSignatureInput = z.infer<typeof drawnSignatureSchema>;

/* ------------------------------------------------- waiver documents ------- */

/**
 * The editable waiver document. Validated here so a malformed one cannot
 * reach the database, where it would break the form for everyone rather than
 * for the one person who saved it.
 *
 * `validateDocument` in `waiver-doc.ts` carries the rules that need to see
 * the whole document at once (duplicate field names, a rule pointing at a
 * field that comes later, a waiver with nothing to sign); this schema is
 * only the shape of a block.
 */
const waiverVisibilityRuleSchema = z.object({
  fieldKey: z.string().trim().min(1).max(60),
  operator: z.enum(WAIVER_VISIBILITY_OPERATORS),
  value: z.union([z.string().max(200), z.number(), z.boolean()]),
});

const waiverContentBlockSchema = z.object({
  id: z.string().trim().min(1).max(40),
  type: z.literal('content'),
  heading: z.string().trim().max(200).optional(),
  body: z.string().max(20000).default(''),
  containerStyle: z.enum(WAIVER_CONTAINER_STYLES).optional(),
  visibleIf: waiverVisibilityRuleSchema.optional(),
});

const waiverFieldBlockSchema = z.object({
  id: z.string().trim().min(1).max(40),
  type: z.enum(WAIVER_FIELD_TYPES),
  key: z.string().trim().regex(/^[a-z0-9_]+$/i, 'Use letters, numbers or underscores').max(60),
  label: z.string().trim().min(1, 'Every field needs a label').max(500),
  required: z.boolean().default(false),
  helpText: z.string().trim().max(500).optional(),
  placeholder: z.string().trim().max(120).optional(),
  options: z.array(z.string().trim().min(1).max(200)).max(50).optional(),
  signerRole: z.enum(WAIVER_SIGNER_ROLES).optional(),
  signatureMode: z.enum(WAIVER_SIGNATURE_MODES).optional(),
  visibleIf: waiverVisibilityRuleSchema.optional(),
});

export const waiverDocumentSchema = z.object({
  blocks: z.array(z.union([waiverContentBlockSchema, waiverFieldBlockSchema])).max(200),
});

/** One field's answer. Marks carry their strokes; everything else is a value. */
export const waiverMarkAnswerSchema = z.object({
  typedName: z.string().trim().max(120).default(''),
  strokes: z.array(z.array(strokePointSchema).max(4000)).max(200).optional(),
  aspect: z.number().min(0.2).max(20).optional(),
});

export const waiverAnswersSchema = z.record(
  z.string().max(60),
  z.union([z.string().max(5000), z.number(), z.boolean(), waiverMarkAnswerSchema, z.null()]),
);

/**
 * Signing a document-backed waiver. The whole-waiver `typedName`/`strokes`
 * stay optional because a document can carry its own signature fields
 * instead; the API requires one or the other, having read the document.
 */
export const signWaiverDocumentSchema = z.object({
  memberId: uuid.optional(),
  answers: waiverAnswersSchema.default({}),
  typedName: z.string().trim().max(120).optional(),
  strokes: z.array(z.array(strokePointSchema).max(4000)).max(200).optional(),
  aspect: z.number().min(0.2).max(20).optional(),
  guardianName: nullable(z.string().trim().max(120)),
});

/**
 * Completing a waiver from the public link, where nobody is signed in.
 *
 * The signer says who they are. The API decides what that means — it will
 * attach the signature to an existing member only on an exact match of both
 * email and date of birth, and otherwise leaves it for the office to link.
 * Nothing here is trusted to clear anybody on its own.
 */
export const publicWaiverSigningSchema = signWaiverDocumentSchema.omit({ memberId: true }).extend({
  firstName: nonEmpty.max(80),
  lastName: nonEmpty.max(80),
  email,
  dateOfBirth: nullable(dateOnly),
  phone: nullable(phone),
});

export type WaiverDocumentInput = z.infer<typeof waiverDocumentSchema>;
export type SignWaiverDocumentInput = z.infer<typeof signWaiverDocumentSchema>;
export type PublicWaiverSigningInput = z.infer<typeof publicWaiverSigningSchema>;

/* ------------------------------------------------- online registration ---- */

/**
 * The public registration form. Validated identically on the client and the
 * server; the server is the enforcer.
 */
export const publicRegistrationSchema = z.object({
  firstName: nonEmpty.max(80),
  lastName: nonEmpty.max(80),
  email,
  phone: nullable(phone),
  address: nullable(z.string().trim().max(200)),
  city: nullable(z.string().trim().max(80)),
  province: nullable(z.string().trim().max(40)),
  postalCode: nullable(z.string().trim().max(12)),
  dateOfBirth: nullable(dateOnly),
  emergencyContactName: nullable(z.string().trim().max(120)),
  emergencyContactPhone: nullable(phone),
  accessibilityNotes: nullable(z.string().trim().max(500)),
  notes: nullable(z.string().trim().max(2000)),

  categoryCode: nonEmpty.max(40),
  /** At least one league, or explicitly signing up as a spare. */
  leagueIds: z.array(uuid).max(13).default([]),
  positionPref: nullable(z.enum(POSITION)),
  isSpare: z.boolean().default(false),
  isReturning: z.boolean().default(false),

  /**
   * Every applicable waiver must be ticked. The server re-derives which apply
   * and refuses the submission if any is missing, so a tampered form cannot
   * skip one.
   */
  waivers: z.array(z.object({
    requirementId: uuid,
    accepted: z.literal(true),
    /** Kept for the office's own entry path, which has no canvas. */
    signedByName: nullable(z.string().trim().max(120)),
    /** A drawn signature. Required on the public form; the server checks. */
    signature: drawnSignatureSchema.partial({ guardianName: true }).optional(),
    /**
     * The waiver's own fields, for a waiver that is a document. The server
     * validates these against the document it holds and derives the signature
     * from whichever field the waiver asks for one in, so `signature` above is
     * only used by a waiver that is still a link to a PDF.
     */
    answers: waiverAnswersSchema.optional(),
  })).min(1, 'The waivers must be accepted'),

  paymentChoice: z.enum(PAYMENT_CHOICE).default('later'),
}).refine((v) => v.leagueIds.length > 0 || v.isSpare, {
  message: 'Choose at least one league, or sign up as a spare',
  path: ['leagueIds'],
});

export const registrationReviewSchema = z.object({
  note: nullable(z.string().trim().max(1000)),
  /** Correct the applicant's details on the way in, if the office spots something. */
  overrides: z.object({
    firstName: z.string().trim().max(80).optional(),
    lastName: z.string().trim().max(80).optional(),
    email: email.optional(),
    categoryCode: z.string().trim().max(40).optional(),
    feeCents: cents.optional(),
  }).default({}),
});

export const registrationQuerySchema = paginationSchema.extend({
  status: z.enum(REGISTRATION_STATUS).optional(),
  seasonId: uuid.optional(),
  q: z.string().trim().max(120).optional(),
});

export type PublicRegistrationInput = z.infer<typeof publicRegistrationSchema>;
export type RegistrationReviewInput = z.infer<typeof registrationReviewSchema>;

/* -------------------------------------------------------------- messaging -- */

/** A message body. Generous, but not a place to paste a novel. */
const messageBody = z.string().trim().min(1, 'Write a message').max(4000);

/** A member (or the office) starting a new conversation. */
export const newThreadSchema = z.object({
  subject: z.string().trim().min(3, 'Give it a short subject').max(140),
  body: messageBody,
  /**
   * Office only: which member the thread is with. A member always opens a
   * thread about themselves, and the server ignores this from them.
   */
  memberId: uuid.optional(),
});

export const replySchema = z.object({
  body: messageBody,
  /** Office only: close the thread with this reply. */
  close: z.boolean().default(false),
});

export const threadQuerySchema = z.object({
  state: z.enum(['open', 'answered', 'closed', 'waiting', 'all']).default('waiting'),
  memberId: uuid.optional(),
  q: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(25),
});

export type NewThreadInput = z.infer<typeof newThreadSchema>;
export type ReplyInput = z.infer<typeof replySchema>;
export type ThreadQuery = z.infer<typeof threadQuerySchema>;

/* ------------------------------------------------- check-in & door access -- */

/**
 * A scanned QR payload: `LCC1.<code>.<step>.<mac>`.
 *
 * Validated by shape here so a scanner that read a bus ticket is rejected
 * before it reaches the database. The server still verifies the MAC.
 */
export const checkinPayloadSchema = z.object({
  payload: z.string().trim().min(8).max(200),
  /** What the member appears to have turned up for, if the desk knows. */
  drawId: uuid.optional(),
  note: nullable(z.string().trim().max(300)),
});

/** A member scanning the printed code by a door. */
export const checkinStationScanSchema = z.object({
  /** Accepts the bare code or the whole scanned URL; the server pulls it out. */
  station: z.string().trim().min(3).max(300),
});

/** The office ticking somebody off by hand, for a member with no phone. */
export const checkinManualSchema = z.object({
  memberId: uuid,
  drawId: uuid.optional(),
  note: nullable(z.string().trim().max(300)),
});

export const checkinStationSchema = z.object({
  name: nonEmpty.max(80),
  location: nullable(z.string().trim().max(120)),
  requiresDraw: z.boolean().default(false),
  windowMinutes: z.number().int().min(5).max(720).default(90),
  isActive: z.boolean().default(true),
});

export const checkinQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  memberId: uuid.optional(),
  drawId: uuid.optional(),
  method: z.enum(CHECKIN_METHOD).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});

const accessSchedule = {
  /** 0 = Sunday, matching Postgres `extract(dow)`. */
  daysOfWeek: nullable(z.array(z.number().int().min(0).max(6)).min(1).max(7)),
  startsAt: nullable(hhmm),
  endsAt: nullable(hhmm),
};

export const accessDoorSchema = z.object({
  name: nonEmpty.max(80),
  slug: z.string().trim().regex(/^[a-z0-9-]{2,40}$/, 'Lower case, digits and hyphens'),
  provider: z.enum(ACCESS_PROVIDER).default('manual'),
  providerRef: nullable(z.string().trim().max(120)),
  providerSiteRef: nullable(z.string().trim().max(120)),
  isActive: z.boolean().default(true),
  allowsRemoteUnlock: z.boolean().default(false),
  notes: nullable(z.string().trim().max(500)),
});

export const accessGroupSchema = z.object({
  name: nonEmpty.max(80),
  slug: z.string().trim().regex(/^[a-z0-9-]{2,40}$/, 'Lower case, digits and hyphens'),
  description: nullable(z.string().trim().max(500)),
  providerRef: nullable(z.string().trim().max(120)),
  autoRoles: z.array(z.enum(ROLES)).default([]),
  autoAllMembers: z.boolean().default(false),
  requiresMembership: z.boolean().default(true),
  requiresWaivers: z.boolean().default(false),
  isActive: z.boolean().default(true),
  doors: z.array(z.object({ doorId: uuid, ...accessSchedule })).default([]),
});

export const accessGroupMemberSchema = z.object({
  memberIds: z.array(uuid).min(1, 'Choose at least one member'),
  expiresOn: nullable(dateOnly),
  reason: nullable(z.string().trim().max(200)),
});

/**
 * Enrolling a fob or card.
 *
 * A card number is digits with the odd separator on the label; it is
 * normalised to digits before it is stored so "0012 3456" and "00123456"
 * cannot both be enrolled as different cards.
 */
export const accessCredentialSchema = z.object({
  memberId: uuid,
  kind: z.enum(ACCESS_CREDENTIAL_KIND).default('fob'),
  label: nullable(z.string().trim().max(60)),
  cardNumber: nullable(
    z.string().trim().max(40)
      .transform((v) => v.replace(/[\s-]/g, ''))
      .refine((v) => v === '' || /^\d{1,20}$/.test(v), 'Card numbers are digits only'),
  ),
  facilityCode: nullable(
    z.string().trim().max(10)
      .refine((v) => v === '' || /^\d{1,5}$/.test(v), 'Facility codes are digits only'),
  ),
  issuedOn: nullable(dateOnly),
  notes: nullable(z.string().trim().max(500)),
}).refine((v) => v.kind === 'mobile' || Boolean(v.cardNumber), {
  message: 'A card or fob needs its number',
  path: ['cardNumber'],
});

export const accessCredentialStateSchema = z.object({
  state: z.enum(ACCESS_CREDENTIAL_STATE),
  reason: nullable(z.string().trim().max(200)),
});

export const accessEventQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  doorId: uuid.optional(),
  memberId: uuid.optional(),
  decision: z.enum(ACCESS_DECISION).optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});

/** Popping a door from the office. Requires a reason: it is logged. */
export const accessUnlockSchema = z.object({
  reason: nonEmpty.max(200),
});

export type CheckinPayloadInput = z.infer<typeof checkinPayloadSchema>;
export type CheckinStationInput = z.infer<typeof checkinStationSchema>;
export type AccessDoorInput = z.infer<typeof accessDoorSchema>;
export type AccessGroupInput = z.infer<typeof accessGroupSchema>;
export type AccessCredentialInput = z.infer<typeof accessCredentialSchema>;
